// Build a map of parent ID => [child items]

import { Generation } from '@cube3/common/model/schema/resources/generation';
import { sortByCreatedDate } from '../../TagManagement/utils/sort';

// { G1: [G2,G5], G2: [G3], G3: [G4], G5: [G6], G7: [G8] }
const mapChildren = (data: Generation[]) => {
  const childrenMap = new Map();

  for (const item of data) {
    const parentId = item.relationships?.reference_generation?.id;
    if (parentId) {
      if (!childrenMap.has(parentId)) {
        childrenMap.set(parentId, []);
      }
      childrenMap.get(parentId).push(item);
    }
  }
  return childrenMap;
};

/** expect output: 
 * {
    G1: { G2: [G3,G4], G5: [G6] },
    G7: { G8: [] },
    G9: {}
}
*/
export const buildGenerationTree = (data: Generation[]) => {
  const childrenMap = mapChildren(data);

  // Helper to collect all descendants of a node
  const collectDescendants = (startId: string) => {
    const descendants = [];
    const visit = (id) => {
      const children = childrenMap.get(id) || [];
      children.forEach((child) => {
        // extend `rootId` attribute for later use
        descendants.push({ ...child, rootId: startId });
        visit(child.id);
      });
    };

    visit(startId);
    return descendants;
  };
  // Build result from root nodes (no parent)
  const roots = data.filter(
    (item) => !item.relationships?.reference_generation
  );

  const tree = roots.reduce((acc, root) => {
    const rootId = root.id;
    acc[rootId] = {};

    for (const child of childrenMap.get(rootId) || []) {
      acc[rootId][child.id] = collectDescendants(child.id);
    }

    return acc;
  }, {});

  return tree;
};

const getParentId = (item: Generation) =>
  item?.relationships.reference_generation?.id;

export const getPrompt = (
  generations: Generation[],
  newlySelected: Generation,
  prevSelected: Generation
) => {
  let prompt = '';
  let gen = prevSelected;

  while (newlySelected && gen && getParentId(gen) !== newlySelected.id) {
    // if we reach the toplevel without finding a direct descendant
    // return null
    if (!getParentId(gen)) {
      gen = null;
      break;
    }
    gen = generations.find((ge) => ge.id === getParentId(gen));
  }
  // we've found a direct descendant in the branch
  if (gen) {
    prompt = gen.prompt;
    // we find a direct descendant outside the branch
  } else {
    prompt =
      sortByCreatedDate(
        generations.filter((g) => getParentId(g) === newlySelected.id)
      )[0]?.prompt || '';
  }

  return prompt;
};
